/**
 * @fileoverview Assignment Validation Hook
 * 
 * Provides validation logic for knowledge base assignments including
 * business rules, constraints, and data integrity checks.
 * 
 * Requirements: 4.1, 4.2
 */

import { useCallback, useMemo } from 'react';

/**
 * Hook for validating knowledge base assignments
 * 
 * @param {KnowledgeBaseAssignmentConfig} config - Module configuration
 * @returns {Object} Validation utilities and functions
 */
export function useAssignmentValidation(config) {

  // Get validation rules based on context
  const validationRules = useMemo(() => {
    const baseRules = {
      maxAssignmentsPerFile: 10,
      maxFilesPerParrot: 50,
      allowDuplicateAssignments: false,
      requireFileValidation: true,
      requireParrotValidation: true
    };

    // Context-specific rules
    switch (config.context) {
      case 'admin-kb':
        return {
          ...baseRules,
          maxAssignmentsPerFile: 100,
          maxFilesPerParrot: 200,
          allowDuplicateAssignments: false
        };
      
      case 'parrot-creation':
        return {
          ...baseRules,
          maxFilesPerParrot: 20,
          allowDuplicateAssignments: false
        };
      
      case 'parrot-editing':
        return {
          ...baseRules,
          maxFilesPerParrot: 50,
          allowDuplicateAssignments: false
        };
      
      case 'user-kb-dashboard':
      default:
        return baseRules;
    }
  }, [config.context]);

  // Validate file for assignment
  const validateFile = useCallback((file) => {
    const errors = [];

    if (!file) {
      errors.push('File is required');
      return { valid: false, errors };
    }

    if (!file.id) {
      errors.push('File ID is required');
    }

    if (!file.name) {
      errors.push('File name is required');
    }

    // Check file status
    if (file.status === 'processing') {
      errors.push('File is still being processed and cannot be assigned');
    }

    if (file.status === 'failed') {
      errors.push('File processing failed and cannot be assigned');
    }

    if (file.status === 'deleted') {
      errors.push('File has been deleted and cannot be assigned');
    }

    // Check file type compatibility
    const supportedTypes = [
      'text/plain',
      'text/markdown',
      'application/pdf',
      'text/html',
      'application/json'
    ];

    if (file.mimeType && !supportedTypes.includes(file.mimeType)) {
      errors.push(`File type ${file.mimeType} is not supported for assignments`);
    }

    // Check file size limits
    const maxFileSize = 10 * 1024 * 1024; // 10MB
    if (file.size && file.size > maxFileSize) {
      errors.push('File is too large for assignment (max 10MB)');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }, []);

  // Validate parrot for assignment
  const validateParrot = useCallback((parrot) => {
    const errors = [];

    if (!parrot) {
      errors.push('Parrot is required');
      return { valid: false, errors };
    }

    if (!parrot.id) {
      errors.push('Parrot ID is required');
    }

    if (!parrot.name) {
      errors.push('Parrot name is required');
    }

    // Check parrot status
    if (parrot.status === 'inactive') {
      errors.push('Parrot is inactive and cannot receive new assignments');
    }

    if (parrot.status === 'deleted') {
      errors.push('Parrot has been deleted and cannot receive assignments');
    }

    // Check if parrot supports knowledge base
    if (!parrot.features?.knowledgeBase) {
      errors.push('Parrot does not support knowledge base assignments');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }, []);

  // Validate assignment operation
  const validateAssignment = useCallback(async (fileId, parrotId, isAssigning, existingAssignments = {}) => {
    const errors = [];

    if (!fileId) {
      errors.push('File ID is required');
    }

    if (!parrotId) {
      errors.push('Parrot ID is required');
    }

    if (errors.length > 0) {
      return { valid: false, errors };
    }

    // Check for duplicate assignments
    const fileAssignments = existingAssignments[fileId] || [];
    const existingAssignment = fileAssignments.find(a => a.parrotId === parrotId);

    if (isAssigning && existingAssignment && !validationRules.allowDuplicateAssignments) {
      errors.push('File is already assigned to this parrot');
    }

    if (!isAssigning && !existingAssignment) {
      errors.push('File is not assigned to this parrot');
    }

    // Check assignment limits
    if (isAssigning) {
      // Check max assignments per file
      if (fileAssignments.length >= validationRules.maxAssignmentsPerFile) {
        errors.push(`File has reached maximum assignments limit (${validationRules.maxAssignmentsPerFile})`);
      }

      // Check max files per parrot
      const parrotAssignmentCount = Object.values(existingAssignments)
        .flat()
        .filter(a => a.parrotId === parrotId)
        .length;

      if (parrotAssignmentCount >= validationRules.maxFilesPerParrot) {
        errors.push(`Parrot has reached maximum files limit (${validationRules.maxFilesPerParrot})`);
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }, [validationRules]);

  // Validate batch assignment operation
  const validateBatchAssignment = useCallback(async (operation, fileIds, parrotIds, existingAssignments = {}) => {
    const errors = [];

    if (!operation || !['assign', 'unassign', 'reassign'].includes(operation)) {
      errors.push('Valid operation is required (assign, unassign, reassign)');
    }

    if (!fileIds || fileIds.length === 0) {
      errors.push('At least one file ID is required');
    }

    if (!parrotIds || parrotIds.length === 0) {
      errors.push('At least one parrot ID is required');
    }

    if (errors.length > 0) {
      return { valid: false, errors };
    }

    // Check batch size limits
    const maxBatchSize = config.context === 'admin-kb' ? 100 : 20;
    const totalOperations = fileIds.length * parrotIds.length;

    if (totalOperations > maxBatchSize) {
      errors.push(`Batch operation too large (${totalOperations} operations, max ${maxBatchSize})`);
    }

    // Validate individual assignments
    const individualErrors = [];
    
    for (const fileId of fileIds) {
      for (const parrotId of parrotIds) {
        const validation = await validateAssignment(
          fileId, 
          parrotId, 
          operation === 'assign', 
          existingAssignments
        );
        
        if (!validation.valid) {
          individualErrors.push({
            fileId,
            parrotId,
            errors: validation.errors
          });
        }
      }
    }

    // If more than 50% of operations would fail, reject the batch
    const failureRate = individualErrors.length / totalOperations;
    if (failureRate > 0.5) {
      errors.push(`Too many validation errors in batch (${Math.round(failureRate * 100)}% failure rate)`);
    }

    return {
      valid: errors.length === 0,
      errors,
      individualErrors
    };
  }, [config.context, validateAssignment]);

  // Validate assignment conflict resolution
  const validateConflictResolution = useCallback((conflicts, resolutions) => {
    const errors = [];

    if (!conflicts || conflicts.length === 0) {
      errors.push('No conflicts to resolve');
    }

    if (!resolutions || typeof resolutions !== 'object') {
      errors.push('Resolutions object is required');
    }

    if (errors.length > 0) {
      return { valid: false, errors };
    }

    // Check that all conflicts have resolutions
    for (const conflict of conflicts) {
      const resolution = resolutions[conflict.id];
      
      if (!resolution) {
        errors.push(`No resolution provided for conflict ${conflict.id}`);
        continue;
      }

      // Validate resolution based on conflict type
      switch (conflict.type) {
        case 'duplicate_assignment':
          if (!['skip', 'overwrite', 'merge'].includes(resolution.action)) {
            errors.push(`Invalid resolution action for duplicate assignment: ${resolution.action}`);
          }
          break;
        
        case 'resource_limit':
          if (!['skip', 'increase_limit', 'remove_oldest'].includes(resolution.action)) {
            errors.push(`Invalid resolution action for resource limit: ${resolution.action}`);
          }
          break;
        
        case 'permission_denied':
          if (!['skip', 'request_permission'].includes(resolution.action)) {
            errors.push(`Invalid resolution action for permission denied: ${resolution.action}`);
          }
          break;
        
        case 'sync_conflict':
          if (!['skip', 'force_sync', 'manual_resolve'].includes(resolution.action)) {
            errors.push(`Invalid resolution action for sync conflict: ${resolution.action}`);
          }
          break;
        
        default:
          errors.push(`Unknown conflict type: ${conflict.type}`);
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }, []);

  // Get validation summary for UI display
  const getValidationSummary = useCallback((files, parrots, assignments) => {
    const summary = {
      totalFiles: files.length,
      validFiles: 0,
      totalParrots: parrots.length,
      validParrots: 0,
      totalAssignments: Object.values(assignments).flat().length,
      errors: [],
      warnings: []
    };

    // Validate files
    files.forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        summary.validFiles++;
      } else {
        summary.errors.push(`File ${file.name}: ${validation.errors.join(', ')}`);
      }
    });

    // Validate parrots
    parrots.forEach(parrot => {
      const validation = validateParrot(parrot);
      if (validation.valid) {
        summary.validParrots++;
      } else {
        summary.errors.push(`Parrot ${parrot.name}: ${validation.errors.join(', ')}`);
      }
    });

    // Check for potential issues
    const assignmentCounts = {};
    Object.values(assignments).flat().forEach(assignment => {
      assignmentCounts[assignment.parrotId] = (assignmentCounts[assignment.parrotId] || 0) + 1;
    });

    Object.entries(assignmentCounts).forEach(([parrotId, count]) => {
      const parrot = parrots.find(p => p.id === parrotId);
      const warningThreshold = Math.floor(validationRules.maxFilesPerParrot * 0.8);
      
      if (count > warningThreshold) {
        summary.warnings.push(
          `Parrot ${parrot?.name || parrotId} has ${count} assignments (approaching limit of ${validationRules.maxFilesPerParrot})`
        );
      }
    });

    return summary;
  }, [validateFile, validateParrot, validationRules]);

  return {
    validationRules,
    validateFile,
    validateParrot,
    validateAssignment,
    validateBatchAssignment,
    validateConflictResolution,
    getValidationSummary
  };
}

export default useAssignmentValidation;
